const SEC_HOSTNAME_REGEX = /(^|\.)sec\.gov$/i;
const NESTED_ABSOLUTE_URL_REGEX = /^(?:[a-z][a-z0-9+.-]*:)?\/\//i;

const LEGAL_DOCUMENT_FIELDS = [
  { id: 'secRecentFiling', title: 'SEC Recent Filing', field: 'secUrl' },
  {
    id: 'offeringCircular',
    title: 'Offering Circular',
    field: 'offeringCircularUrl',
    rejectOfferingSelfLink: true,
  },
  { id: 'offeringMemorandum', title: 'Offering Memorandum', field: 'offeringDetailsUrl' },
  { id: 'risksRelatedToTheOffering', title: 'Offering Related Risks', field: 'associatedRisksUrl' },
];

const getSearchParam = (searchParams, expectedKey) => {
  for (const [key, value] of searchParams.entries()) {
    if (key.toLowerCase() === expectedKey) {
      return value;
    }
  }

  return null;
};

const decodeRepeatedly = (value) => {
  let decoded = value.trim();

  for (let i = 0; i < 2; i += 1) {
    try {
      const next = decodeURIComponent(decoded);
      if (next === decoded) break;
      decoded = next.trim();
    } catch {
      break;
    }
  }

  return decoded;
};

const looksLikeNestedUrl = (value) => {
  if (!value) return false;
  return NESTED_ABSOLUTE_URL_REGEX.test(decodeRepeatedly(value));
};

const isSecBrowseUrl = (url) => {
  const pathname = url.pathname.toLowerCase().replace(/\/+$/, '');
  return pathname === '/cgi-bin/browse-edgar' || pathname === '/edgar/browse';
};

const hasUsableSecIdentifier = (url) => {
  const cik = getSearchParam(url.searchParams, 'cik');
  const company = getSearchParam(url.searchParams, 'company');

  return [cik, company].some((identifier) => {
    if (identifier === null) return false;

    const trimmedIdentifier = identifier.trim();
    return Boolean(trimmedIdentifier) && !looksLikeNestedUrl(trimmedIdentifier);
  });
};

const normalizeSlug = (slug) => {
  if (typeof slug !== 'string') return '';
  return slug.trim().toLowerCase();
};

const isStartEngineHostname = (hostname) => /(^|\.)startengine\.com$/i.test(hostname);

const isOfferingSelfLink = (parsedUrl, offeringSlug) => {
  const slug = normalizeSlug(offeringSlug);
  if (!slug || !isStartEngineHostname(parsedUrl.hostname)) return false;

  const pathname = parsedUrl.pathname.toLowerCase().replace(/\/+$/, '');
  return pathname === `/offering/${slug}` || pathname === `/${slug}`;
};

export const getLegalDocumentUrl = (url, options = {}) => {
  if (typeof url !== 'string') return '';

  const trimmed = url.trim();
  if (!trimmed) return '';

  let parsedUrl;
  try {
    parsedUrl = new URL(trimmed);
  } catch {
    return '';
  }

  if (parsedUrl.protocol !== 'https:' && parsedUrl.protocol !== 'http:') return '';

  if (options.rejectOfferingSelfLink && isOfferingSelfLink(parsedUrl, options.offeringSlug)) {
    return '';
  }

  if (!SEC_HOSTNAME_REGEX.test(parsedUrl.hostname)) return trimmed;

  const cik = getSearchParam(parsedUrl.searchParams, 'cik');
  if (cik !== null && looksLikeNestedUrl(cik)) return '';

  if (isSecBrowseUrl(parsedUrl) && !hasUsableSecIdentifier(parsedUrl)) return '';

  return trimmed;
};

export const buildLegalDocumentLinks = (offeringData) =>
  LEGAL_DOCUMENT_FIELDS.map(({ id, title, field, rejectOfferingSelfLink }) => ({
    id,
    title,
    url: getLegalDocumentUrl(offeringData?.[field], {
      offeringSlug: offeringData?.slug,
      rejectOfferingSelfLink,
    }),
  })).filter((link) => Boolean(link.url));
